import { inject, Injectable } from '@angular/core';
import { Indicator, xlsxColumnProperties } from '../../models/indicator';
import { TagStore } from '../../stores/tag/tag.state';
import { CategoryStore } from '../../stores/category/category.state';
import { Tag } from '../../models/tag';
import { Category } from '../../models/category';
import { validateIndicatorKeyValue } from '../../validators/key.validator';
import { IndicatorStore } from '../../stores/indicator/indicator.state';
import { MarkdownService } from '../markdown.service';
import { unitMapping } from '../../models/unit';
import { POI } from '../../models/data-layer-type';
import { ImportIndicator } from '../indicator/import-validator.service';
import { validateKeyValue } from '../../validators/district-key.validator';

@Injectable({
  providedIn: 'root',
})
export class PoiImportValidatorService {
  public errors: Record<number, Record<string, boolean>> | false = false;
  public warnings: Record<number, Record<string, boolean>> = {};
  public isInvalid = false;
  public convertAvailable = false;

  private propertiesToConvert: Array<keyof POI> = [
    'description',
    'contact',
    'information',
  ];

  constructor(private markdownService: MarkdownService) {}

  /**
   * Validate POI and map errors / warnings
   * @param poiList Array of POI objects to validate
   * @returns Validated list of POI objects
   */
  validateAndMap(poiList: POI[], existingKeys: string[]): POI[] {
    this.errors = {};
    this.warnings = {};

    if (poiList.length > 0) {
      const keyList: string[] = [];
      poiList.forEach((poi, index) => {
        poi = this.convertFromMarkdown(poi);
        // Validate districtKey using validateKeyValue
        let districtKeyErrors = validateKeyValue(poi.key ?? '');

        // duplicates
        if (keyList.includes(poi.key)) {
          districtKeyErrors
            ? (districtKeyErrors['duplicate'] = true)
            : (districtKeyErrors = { duplicate: true });
        }

        if (districtKeyErrors) {
          this.setError(index, 'districtKey');
          Object.keys(districtKeyErrors).forEach(errorKey => {
            this.setError(index, errorKey);
          });
          poi.validation = 'danger';
        } else {
          poi.validation = existingKeys.includes(poi.key) ? 'info' : 'success';
          keyList.push(poi.key);
        }

        if (!poi.name) {
          this.setError(index, 'name');
          poi.validation = 'danger';
        }

        if (!poi.geoPositionX || !poi.geoPositionY) {
          this.setWarning(index, 'geoPositionX', 'geoPositionY');
          this.convertAvailable = true;
          if (poi.validation !== 'danger') poi.validation = 'warning';
        } else {
          this.clearWarning(index, 'geoPositionX', 'geoPositionY');
        }
      });
    }

    this.isInvalid =
      this.errors && Object.keys(this.errors).length > 0 ? true : false;
    this.errors = Object.keys(this.errors).length > 0 ? this.errors : false;

    return poiList;
  }

  private setError(index: number, key: string): void {
    if (!this.errors) this.errors = {};
    if (!this.errors[index]) this.errors[index] = {};
    this.errors[index][key] = true;
  }

  private setWarning(index: number, ...keys: string[]): void {
    if (!this.warnings[index]) this.warnings[index] = {};
    keys.forEach(key => {
      this.warnings[index][key] = true;
    });
  }

  private clearWarning(index: number, ...keys: string[]): void {
    if (this.warnings[index]) {
      keys.forEach(key => {
        delete this.warnings[index][key];
      });
    }
  }

  private convertFromMarkdown(poi: POI) {
    for (const property of this.propertiesToConvert) {
      const value = poi[property];
      if (typeof value === 'string') {
        poi[property] = this.markdownService.convertToHtml(value);
      } else {
        poi[property] = ''; // Assign a default value if not a string
      }
    }

    return poi;
  }
}
